/**
 * Reveals elements marked `data-scroll-reveal` with a fade + rise-in as they enter the
 * viewport. Runs once per element (unobserves after reveal), skips entirely when the visitor
 * prefers reduced motion, and falls back to revealing everything if IntersectionObserver is
 * unavailable or the observer somehow misses a target.
 */
(() => {
  'use strict';

  const SELECTOR = '[data-scroll-reveal]';
  const VISIBLE_CLASS = 'scroll-reveal--visible';

  const reveal = (element) => element.classList.add(VISIBLE_CLASS);

  const revealAll = () => {
    document.querySelectorAll(SELECTOR).forEach(reveal);
  };

  const observeAll = () => {
    const targets = document.querySelectorAll(`${SELECTOR}:not(.${VISIBLE_CLASS})`);
    if (!targets.length) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      targets.forEach(reveal);
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            reveal(entry.target);
            obs.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );

    targets.forEach((element) => observer.observe(element));
  };

  try {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observeAll);
    } else {
      observeAll();
    }

    // Re-scan when the theme editor swaps section markup in.
    document.addEventListener('shopify:section:load', observeAll);

    // Safety net: reveal anything the observer missed once the page has settled.
    window.addEventListener('load', () => setTimeout(revealAll, 3000));
  } catch (error) {
    revealAll();
  }
})();
